/**
 * Wizard de creacion de Perfil de Configuracion (ADR_012 DFCMGMT-05).
 *
 * 6 pasos:
 *   1. Identidad: name + description + device_type
 *   2. Equipos: seleccion del inventario propio (tabla devices)
 *   3. Recoleccion: collection_method + credentials_ref (preseleccionados)
 *   4. Campos: checkboxes del fields_catalog del device_type
 *   5. Schedule: presets + custom cron + preview
 *   6. Revisar: summary read-only + boton "Crear perfil"
 *
 * Al final POST a /api/v1/config/profiles, luego POST devices.
 * Redirect a /admin/configuracion/{id} (drilldown del perfil — PR-E).
 */
import {
  AlertCircle,
  Check,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import { ScheduleBuilder } from "@/components/ScheduleBuilder";
import {
  useAddDevicesToProfile,
  useConfigDevices,
  useCreateConfigurationProfile,
  useDeviceTypeCatalog,
  useFieldCatalog,
} from "@/hooks/useConfigProfiles";
import { cn } from "@/lib/utils";
import type { DeviceTypeCatalogEntry } from "@/api/types";

// ────────────────────────────────────────────────────────────────────────────
// Constantes

// PR-CMgmt-H: el catálogo viene del endpoint `/api/v1/config/device-types`.
// Antes era hardcoded. Helpers para consultarlo:

function findDeviceType(
  catalog: DeviceTypeCatalogEntry[] | undefined,
  value: string,
): DeviceTypeCatalogEntry | undefined {
  return catalog?.find((t) => t.value === value);
}

// ────────────────────────────────────────────────────────────────────────────
// State del wizard

interface WizardState {
  // Paso 1
  name: string;
  description: string;
  device_type: string;
  // Paso 2 — Inventario propio (tabla devices)
  selected_device_ids: Set<string>;
  // Paso 3
  collection_method: string;
  credentials_ref: string;
  // Paso 4
  fields_to_audit: Set<string>;
  // Paso 5 (PR-CMgmt-H: ScheduleBuilder produce el cron directo)
  schedule_cron: string;
}

function initialState(): WizardState {
  return {
    name: "",
    description: "",
    device_type: "",
    selected_device_ids: new Set(),
    collection_method: "",
    credentials_ref: "",
    fields_to_audit: new Set(),
    schedule_cron: "0 3 * * *",
  };
}

function effectiveCron(state: WizardState): string {
  return state.schedule_cron.trim();
}

// ────────────────────────────────────────────────────────────────────────────
// Validacion por paso

function validateStep1(s: WizardState): string[] {
  const errs: string[] = [];
  if (s.name.trim().length < 3)
    errs.push("El nombre debe tener al menos 3 caracteres.");
  if (!s.device_type) errs.push("Selecciona un tipo de equipo.");
  return errs;
}

function validateStep2(s: WizardState): string[] {
  const errs: string[] = [];
  if (s.selected_device_ids.size === 0)
    errs.push("Marca al menos un equipo del inventario.");
  return errs;
}

function validateStep3(s: WizardState): string[] {
  const errs: string[] = [];
  if (!s.collection_method) errs.push("Selecciona el método de recolección.");
  if (!s.credentials_ref) errs.push("Selecciona la referencia de credenciales.");
  return errs;
}

function validateStep4(s: WizardState): string[] {
  const errs: string[] = [];
  if (s.fields_to_audit.size === 0)
    errs.push("Marca al menos un campo a auditar.");
  return errs;
}

function validateStep5(s: WizardState): string[] {
  const errs: string[] = [];
  const cron = effectiveCron(s);
  if (!cron) errs.push("Define un schedule cron.");
  else if (!/^(\S+\s+){4}\S+$/.test(cron))
    errs.push("Cron inválido. Debe tener 5 campos separados por espacios.");
  return errs;
}

// ────────────────────────────────────────────────────────────────────────────
// Componente principal

export function NewConfigProfilePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5 | 6>(1);
  const [state, setState] = useState<WizardState>(initialState());
  const [errors, setErrors] = useState<string[]>([]);

  const createMut = useCreateConfigurationProfile();
  const addDevicesMut = useAddDevicesToProfile();

  const onNext = () => {
    let errs: string[] = [];
    if (step === 1) errs = validateStep1(state);
    if (step === 2) errs = validateStep2(state);
    if (step === 3) errs = validateStep3(state);
    if (step === 4) errs = validateStep4(state);
    if (step === 5) errs = validateStep5(state);
    setErrors(errs);
    if (errs.length === 0 && step < 6) {
      setStep((step + 1) as typeof step);
    }
  };

  const onBack = () => {
    setErrors([]);
    if (step > 1) setStep((step - 1) as typeof step);
  };

  const handleCreate = async () => {
    setErrors([]);
    try {
      const profile = await createMut.mutateAsync({
        name: state.name.trim(),
        description: state.description.trim(),
        device_type: state.device_type,
        collection_method: state.collection_method,
        credentials_ref: state.credentials_ref,
        fields_to_audit: Array.from(state.fields_to_audit),
        schedule_cron: effectiveCron(state),
        zabbix_host_group: null,
      });
      // Add devices (best effort — si falla, el perfil ya existe, el admin
      // puede agregarlos manualmente despues).
      const idsToAdd = Array.from(state.selected_device_ids);
      if (idsToAdd.length > 0) {
        try {
          await addDevicesMut.mutateAsync({
            profileId: profile.id,
            deviceIds: idsToAdd,
          });
        } catch {
          // Silencioso. El usuario llega al drilldown y ve el perfil sin equipos;
          // puede importarlos desde ahi.
        }
      }
      navigate(`/admin/configuracion?profile_id=${profile.id}`);
    } catch (err) {
      setErrors([err instanceof ApiError ? err.message : "Error al crear."]);
    }
  };

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">
          Nuevo perfil de configuración
        </h1>
        <p className="text-sm text-muted-foreground">
          Define un grupo de equipos a auditar, qué campos capturar y cada
          cuándo. Cuando termines, el scheduler empieza a respetarlo.
        </p>
      </header>

      <StepNav current={step} />

      {errors.length > 0 && <ErrorBox errors={errors} />}

      <div className="rounded-lg border bg-card p-6">
        {step === 1 && <Step1Identity state={state} setState={setState} />}
        {step === 2 && <Step2Devices state={state} setState={setState} />}
        {step === 3 && <Step3Collection state={state} setState={setState} />}
        {step === 4 && <Step4Fields state={state} setState={setState} />}
        {step === 5 && <Step5Schedule state={state} setState={setState} />}
        {step === 6 && <Step6Review state={state} />}
      </div>

      <footer className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          disabled={step === 1}
          className="inline-flex items-center gap-1 rounded-md border px-4 py-2 text-sm font-medium transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronLeft className="size-4" />
          Atrás
        </button>
        {step < 6 ? (
          <button
            type="button"
            onClick={onNext}
            className="inline-flex items-center gap-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Siguiente
            <ChevronRight className="size-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleCreate}
            disabled={createMut.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {createMut.isPending && <Loader2 className="size-4 animate-spin" />}
            Crear perfil
          </button>
        )}
      </footer>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Sub-componentes

function StepNav({ current }: { current: number }) {
  const steps = [
    "Identidad",
    "Equipos",
    "Recolección",
    "Campos",
    "Schedule",
    "Revisar",
  ];
  return (
    <ol className="flex flex-wrap gap-2" role="tablist">
      {steps.map((label, i) => {
        const num = i + 1;
        const state =
          current === num ? "current" : current > num ? "done" : "pending";
        return (
          <li
            key={num}
            role="tab"
            aria-selected={state === "current"}
            className={cn(
              "flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium",
              state === "current" &&
                "border-primary bg-primary text-primary-foreground",
              state === "done" &&
                "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
              state === "pending" &&
                "border-border bg-card text-muted-foreground",
            )}
          >
            <span
              className={cn(
                "flex size-5 items-center justify-center rounded-full text-[10px] font-semibold",
                state === "current" && "bg-primary-foreground text-primary",
                state === "done" && "bg-emerald-600 text-white",
                state === "pending" && "bg-muted text-muted-foreground",
              )}
            >
              {state === "done" ? <Check className="size-3" /> : num}
            </span>
            {label}
          </li>
        );
      })}
    </ol>
  );
}

function ErrorBox({ errors }: { errors: string[] }) {
  return (
    <div
      role="alert"
      className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
    >
      <div className="mb-1 flex items-center gap-2 font-medium">
        <AlertCircle className="size-4" />
        Corrige antes de continuar:
      </div>
      <ul className="list-inside list-disc space-y-0.5 text-xs">
        {errors.map((e, i) => (
          <li key={i}>{e}</li>
        ))}
      </ul>
    </div>
  );
}

// ── Paso 1 ───────────────────────────────────────────────────────────────

function Step1Identity({
  state,
  setState,
}: {
  state: WizardState;
  setState: (s: WizardState) => void;
}) {
  const catalog = useDeviceTypeCatalog();
  const items = catalog.data?.items ?? [];

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="profile-name" className="mb-1 block text-sm font-medium">
          Nombre del perfil
        </label>
        <input
          id="profile-name"
          type="text"
          value={state.name}
          onChange={(e) => setState({ ...state, name: e.target.value })}
          placeholder="Ej. Biométricos Hikvision Holanda"
          maxLength={128}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>

      <div>
        <label
          htmlFor="profile-description"
          className="mb-1 block text-sm font-medium"
        >
          Descripción (opcional)
        </label>
        <textarea
          id="profile-description"
          value={state.description}
          onChange={(e) =>
            setState({ ...state, description: e.target.value })
          }
          rows={3}
          maxLength={500}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>

      <div>
        <div className="mb-2 text-sm font-medium">Tipo de equipo</div>
        {catalog.isPending && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="size-3 animate-spin" />
            Cargando tipos disponibles…
          </div>
        )}
        <div className="grid gap-2">
          {items.map((t) => {
            const active = state.device_type === t.value;
            const disabled = !t.implemented;
            return (
              <button
                key={t.value}
                type="button"
                disabled={disabled}
                onClick={() =>
                  setState({
                    ...state,
                    device_type: t.value,
                    // Pre-selecciona collection_method y creds del default
                    collection_method: t.methods[0]?.value ?? "",
                    credentials_ref:
                      t.credentials_by_method[t.methods[0]?.value ?? ""]?.[0]
                        ?.value ?? "",
                    fields_to_audit: new Set(),
                  })
                }
                className={cn(
                  "rounded-md border px-4 py-3 text-left transition-colors",
                  disabled
                    ? "cursor-not-allowed border-dashed border-border bg-muted/30 opacity-60"
                    : active
                      ? "border-primary bg-primary/5"
                      : "border-border hover:bg-accent",
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-medium">{t.label}</div>
                  {!t.implemented && (
                    <span className="rounded-md bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:text-amber-400">
                      Próximamente
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t.description}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Paso 2 ───────────────────────────────────────────────────────────────

function Step2Devices({
  state,
  setState,
}: {
  state: WizardState;
  setState: (s: WizardState) => void;
}) {
  return <Step2DevicesInventory state={state} setState={setState} />;
}

function Step2DevicesInventory({
  state,
  setState,
}: {
  state: WizardState;
  setState: (s: WizardState) => void;
}) {
  const devicesQ = useConfigDevices({ device_type: state.device_type, per_page: 200 });
  const items = devicesQ.data?.items ?? [];

  const toggle = (id: string) => {
    const next = new Set(state.selected_device_ids);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setState({ ...state, selected_device_ids: next });
  };

  const selectAll = () =>
    setState({ ...state, selected_device_ids: new Set(items.map((d) => d.id)) });

  const clearAll = () =>
    setState({ ...state, selected_device_ids: new Set() });

  // Etiqueta de posicion legible
  const roleLabel: Record<string, string> = {
    principal_entrada: "Entrada",
    principal_salida: "Salida",
    interior_entrada: "Int. Entrada",
    interior_salida: "Int. Salida",
    segura: "Segura",
    p3_entrada: "P3 Entrada",
    p3_salida: "P3 Salida",
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium">Equipos del inventario</p>
        <p className="text-xs text-muted-foreground">
          Selecciona los equipos a incluir en este perfil de auditoría.
          Podés seleccionar todos o marcar individualmente.
        </p>
      </div>

      {devicesQ.isPending && (
        <p className="text-xs text-muted-foreground">Cargando inventario…</p>
      )}
      {devicesQ.error && (
        <p className="text-xs text-destructive">No se pudo cargar el inventario.</p>
      )}

      {items.length > 0 && (
        <>
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            <div className="flex items-center justify-between">
              <span>
                <b className="text-foreground">{items.length}</b> equipos ·{" "}
                <b className="text-foreground">{state.selected_device_ids.size}</b> seleccionados
              </span>
              <span className="flex gap-2">
                <button type="button" onClick={selectAll} className="text-primary hover:underline">
                  Todos
                </button>
                <button type="button" onClick={clearAll} className="text-muted-foreground hover:underline">
                  Ninguno
                </button>
              </span>
            </div>
          </div>

          <div className="max-h-[300px] overflow-y-auto rounded-md border">
            {items.map((d) => {
              const checked = state.selected_device_ids.has(d.id);
              return (
                <label
                  key={d.id}
                  className={cn(
                    "flex cursor-pointer items-center gap-3 border-b px-3 py-2 text-xs last:border-b-0",
                    checked && "bg-primary/5",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(d.id)}
                    className="size-4"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">
                      {d.site_name ?? d.site_id} — {roleLabel[d.role ?? ""] ?? d.role}
                    </div>
                    <div className="truncate font-mono text-muted-foreground">
                      {d.ip} · {d.model ?? d.device_type}
                    </div>
                  </div>
                  <span
                    className={cn(
                      "rounded-md px-2 py-0.5 text-[10px] font-medium",
                      d.status === "online"
                        ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                        : "bg-muted text-muted-foreground",
                    )}
                  >
                    {d.status}
                  </span>
                </label>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ── Paso 3 ───────────────────────────────────────────────────────────────

function Step3Collection({
  state,
  setState,
}: {
  state: WizardState;
  setState: (s: WizardState) => void;
}) {
  // PR-CMgmt-H: catálogo dinámico — filtros cascada por device_type/method.
  const catalog = useDeviceTypeCatalog();
  const dtEntry = findDeviceType(catalog.data?.items, state.device_type);
  const methods = dtEntry?.methods ?? [];
  const credsOptions =
    dtEntry?.credentials_by_method[state.collection_method] ?? [];

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium">Método de recolección</p>
        <p className="text-xs text-muted-foreground">
          Pre-seleccionado según el tipo de equipo. Si hay alternativas, puedes
          cambiar.
        </p>
        {methods.length === 0 && (
          <p className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-400">
            Aún no hay métodos de recolección implementados para este tipo de
            equipo. El equipo TiLatina debe agregar la strategy antes de crear
            perfiles.
          </p>
        )}
        <div className="mt-2 grid gap-2">
          {methods.map((m) => {
            const active = state.collection_method === m.value;
            return (
              <button
                key={m.value}
                type="button"
                onClick={() =>
                  setState({
                    ...state,
                    collection_method: m.value,
                    credentials_ref:
                      dtEntry?.credentials_by_method[m.value]?.[0]?.value ??
                      "",
                  })
                }
                className={cn(
                  "rounded-md border px-3 py-2 text-left text-sm transition-colors",
                  active ? "border-primary bg-primary/5" : "hover:bg-accent",
                )}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="text-sm font-medium">Credenciales</p>
        <p className="text-xs text-muted-foreground">
          Referencia a las variables del .env del despliegue. No se editan
          credenciales desde la UI.
        </p>
        {credsOptions.length === 0 && methods.length > 0 && (
          <p className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-400">
            No hay credenciales registradas para el método elegido.
          </p>
        )}
        <div className="mt-2 grid gap-2">
          {credsOptions.map((c) => {
            const active = state.credentials_ref === c.value;
            return (
              <button
                key={c.value}
                type="button"
                onClick={() =>
                  setState({ ...state, credentials_ref: c.value })
                }
                className={cn(
                  "rounded-md border px-3 py-2 text-left text-xs font-mono transition-colors",
                  active ? "border-primary bg-primary/5" : "hover:bg-accent",
                )}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Paso 4 ───────────────────────────────────────────────────────────────

function Step4Fields({
  state,
  setState,
}: {
  state: WizardState;
  setState: (s: WizardState) => void;
}) {
  const catalog = useFieldCatalog(state.device_type || null);

  const grouped = useMemo(() => {
    const items = catalog.data?.items ?? [];
    const map = new Map<string, typeof items>();
    for (const it of items) {
      if (!map.has(it.category)) map.set(it.category, []);
      map.get(it.category)!.push(it);
    }
    return Array.from(map.entries());
  }, [catalog.data]);

  const toggle = (key: string) => {
    const next = new Set(state.fields_to_audit);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setState({ ...state, fields_to_audit: next });
  };

  const allKeys = catalog.data?.items.map((i) => i.key) ?? [];

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium">Campos a auditar</p>
        <p className="text-xs text-muted-foreground">
          Solo se persistirán snapshots con estos campos. Si después cambias el
          catálogo, los snapshots antiguos no se reprocesan.
        </p>
      </div>

      {catalog.isPending && (
        <div className="text-xs text-muted-foreground">
          Cargando catálogo…
        </div>
      )}

      {catalog.data && (
        <>
          <div className="flex gap-2 text-xs">
            <button
              type="button"
              onClick={() =>
                setState({ ...state, fields_to_audit: new Set(allKeys) })
              }
              className="text-primary hover:underline"
            >
              Marcar todos ({allKeys.length})
            </button>
            <button
              type="button"
              onClick={() =>
                setState({ ...state, fields_to_audit: new Set() })
              }
              className="text-muted-foreground hover:underline"
            >
              Ninguno
            </button>
          </div>

          {grouped.map(([category, items]) => (
            <div key={category}>
              <h3 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
                {category}
              </h3>
              <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                {items.map((it) => {
                  const checked = state.fields_to_audit.has(it.key);
                  return (
                    <label
                      key={it.key}
                      className={cn(
                        "flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-xs transition-colors",
                        checked ? "border-primary bg-primary/5" : "hover:bg-accent",
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(it.key)}
                        className="size-3.5"
                      />
                      <span className="flex-1">{it.label}</span>
                      <span className="font-mono text-[10px] text-muted-foreground">
                        {it.key}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ── Paso 5 ───────────────────────────────────────────────────────────────

function Step5Schedule({
  state,
  setState,
}: {
  state: WizardState;
  setState: (s: WizardState) => void;
}) {
  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium">Cada cuándo se hace la captura</p>
        <p className="text-xs text-muted-foreground">
          Escoge un preset o personaliza días + hora.
        </p>
      </div>

      <ScheduleBuilder
        value={state.schedule_cron}
        onChange={(cron) => setState({ ...state, schedule_cron: cron })}
      />
    </div>
  );
}

// ── Paso 6 ───────────────────────────────────────────────────────────────

function Step6Review({ state }: { state: WizardState }) {
  const catalog = useDeviceTypeCatalog();
  const dtEntry = findDeviceType(catalog.data?.items, state.device_type);
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Revisa antes de crear. El perfil queda en estado <em>activo</em> y el
        scheduler lo evalúa en el próximo minuto.
      </p>

      <dl className="grid grid-cols-1 gap-3 rounded-md border bg-muted/30 p-4 sm:grid-cols-2">
        <SummaryRow label="Nombre" value={state.name} />
        <SummaryRow
          label="Tipo de equipo"
          value={dtEntry?.label ?? state.device_type}
        />
        <SummaryRow
          label="Equipos seleccionados"
          value={`${state.selected_device_ids.size}`}
        />
        <SummaryRow
          label="Método de recolección"
          value={state.collection_method}
        />
        <SummaryRow
          label="Credenciales"
          value={state.credentials_ref}
        />
        <SummaryRow
          label="Campos auditados"
          value={`${state.fields_to_audit.size}`}
        />
        <SummaryRow label="Cron" value={effectiveCron(state)} mono />
      </dl>

      {state.description && (
        <div>
          <div className="mb-1 text-xs uppercase text-muted-foreground">
            Descripción
          </div>
          <div className="rounded-md border bg-background p-3 text-sm">
            {state.description}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs uppercase text-muted-foreground">{label}</dt>
      <dd className={cn("text-sm font-medium", mono && "font-mono")}>
        {value || "—"}
      </dd>
    </div>
  );
}
